import React, { useState, useEffect } from 'react';
import { ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { integrationService } from '../services/integrationService';
import { motion, AnimatePresence } from 'framer-motion';
import { useProjects } from '../contexts/ProjectContext';
import { IntegrationForm, IntegrationsList } from '../components/integrations';

// Integration option data
const integrationOptions = [
  { id: 'wordpress', name: 'WordPress', logo: '/images/integration-logos/wordpress-icon.svg' },
  { id: 'webflow', name: 'Webflow', logo: '/images/integration-logos/webflow-icon.svg' },
  { id: 'notion', name: 'Notion', logo: '/images/integration-logos/notion-icon.svg' },
  { id: 'shopify', name: 'Shopify', logo: '/images/integration-logos/shopify-icon.svg' },
  { id: 'wix', name: 'Wix', logo: '/images/integration-logos/wix-icon.svg' },
  { id: 'wordpress-com', name: 'WordPress.com', logo: '/images/integration-logos/wordpress-com-icon.svg' },
  { id: 'api-webhook', name: 'API Webhook', logo: '/images/integration-logos/webhook-icon.svg' },
];

// Main App Component with Tabs
const IntegrationsPage = () => {
  const { currentProject } = useProjects();
  const [currentView, setCurrentView] = useState('list');
  const [selectedIntegrationType, setSelectedIntegrationType] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);

  // Hide notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
  };

  // Fetch integrations on mount and when project changes
  useEffect(() => {
    if (currentProject?.id) {
      fetchIntegrations();
    }
  }, [currentProject?.id]);

  const fetchIntegrations = async () => {
    if (!currentProject?.id) {
      showNotification('Please select a project first', 'error');
      return;
    }

    try {
      const data = await integrationService.listIntegrations(currentProject.id);
      setIntegrations(data);
    } catch (error) {
      showNotification('Failed to load integrations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIntegration = async (e) => {
    e.preventDefault();

    if (!currentProject?.id) {
      showNotification('Please select a project first', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      let credentials = {};
      let isValid = true;

      // Format credentials based on integration type
      switch (selectedIntegrationType) {
        case 'wordpress':
          credentials = {
            wordpress_url: formData.wordpress_url,
            username: formData.username,
            app_password: formData.app_password,
            publish_status: formData.publish_status
          };

          // Verify WordPress credentials before creating
          try {
            const verifyResult = await integrationService.verifyWordPressCredentials({
              wordpress_url: formData.wordpress_url,
              username: formData.username,
              app_password: formData.app_password
            });

            if (!verifyResult.valid) {
              showNotification(verifyResult.error || 'Invalid WordPress credentials. Please verify before creating.', 'error');
              isValid = false;
            }
          } catch (error) {
            console.error('Error verifying WordPress credentials:', error);
            showNotification('Failed to verify WordPress credentials', 'error');
            isValid = false;
          }
          break;

        case 'webflow':
          credentials = {
            api_token: formData.api_token,
            site_id: formData.site_id,
            collection_id: formData.collection_id,
            publishing_behavior: formData.publishing_behavior
          };
          break;

        case 'notion':
          credentials = {
            access_token: formData.access_token,
            database_id: formData.database_id,
            workspace_id: formData.workspace_id
          };
          break;

        case 'shopify':
          credentials = {
            store_name: formData.store_name,
            access_token: formData.access_token,
            blog_id: formData.blog_id,
            author_name: formData.author_name,
            publishing_status: formData.publishing_status
          };

          // Verify Shopify credentials before creating
          try {
            const verifyResult = await integrationService.verifyShopifyCredentials({
              store_name: formData.store_name,
              access_token: formData.access_token
            });

            if (!verifyResult.valid) {
              showNotification(verifyResult.error || 'Invalid Shopify credentials. Please verify before creating.', 'error');
              isValid = false;
            }
          } catch (error) {
            console.error('Error verifying Shopify credentials:', error);
            showNotification('Failed to verify Shopify credentials', 'error');
            isValid = false;
          }
          break;

        case 'wix':
          credentials = {
            site_id: formData.site_id,
            api_key: formData.api_key,
            blog_id: formData.blog_id,
            publishing_status: formData.publishing_status
          };

          // Verify Wix credentials before creating
          try {
            const verifyResult = await integrationService.verifyWixCredentials({
              site_id: formData.site_id,
              api_key: formData.api_key
            });

            if (!verifyResult.valid) {
              showNotification(verifyResult.error || 'Invalid Wix credentials. Please verify before creating.', 'error');
              isValid = false;
            }
          } catch (error) {
            console.error('Error verifying Wix credentials:', error);
            showNotification('Failed to verify Wix credentials', 'error');
            isValid = false;
          }
          break;

        case 'api-webhook':
          credentials = {
            webhook_endpoint: formData.webhook_endpoint,
            access_token: formData.access_token
          };
          break;
      }

      if (!isValid) {
        setIsSubmitting(false);
        return;
      }

      // Check if we're updating an existing integration or creating a new one
      if (formData.integrationId) {
        // Update existing integration
        const updatedIntegration = await integrationService.updateIntegration(formData.integrationId, {
          name: formData.name,
          status: formData.status || 'active',
          credentials // Pass credentials directly to the updateIntegration function
        });

        // Update the integration in the state
        setIntegrations(prev => prev.map(integration =>
          integration.id === formData.integrationId ? {
            ...integration,
            name: updatedIntegration.name,
            status: updatedIntegration.status,
            // Keep both keys for compatibility across old/new consumers.
            credentials: [{ credentials }],
            integration_credentials: [{ credentials }]
          } : integration
        ));

        showNotification('Integration updated successfully');
      } else {
        // Create new integration
        const newIntegration = await integrationService.createIntegration({
          name: formData.name,
          type: selectedIntegrationType,
          project_id: currentProject.id,
          credentials
        });

        setIntegrations(prev => [...prev, newIntegration]);
        showNotification('Integration created successfully');
      }

      setCurrentView('list');
      setSelectedIntegrationType(null);
      setFormData({});

    } catch (error) {
      showNotification(error.message || `Failed to ${formData.integrationId ? 'update' : 'create'} integration`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteIntegration = async (id) => {
    try {
      await integrationService.deleteIntegration(id);
      setIntegrations(prev => prev.filter(integration => integration.id !== id));
      showNotification('Integration deleted successfully');
    } catch (error) {
      showNotification('Failed to delete integration', 'error');
    }
  };

  const handleToggleStatus = async (id, newStatus) => {
    try {
      await integrationService.updateIntegration(id, { status: newStatus });
      setIntegrations(prev => prev.map(integration =>
        integration.id === id ? { ...integration, status: newStatus } : integration
      ));
      showNotification(`Integration ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      showNotification('Failed to update integration status', 'error');
    }
  };

  const handleEditIntegration = (integration) => {
    // Set up the edit view with the selected integration
    setSelectedIntegrationType(integration.type);

    // Pre-fill the form data with the integration's credentials.
    // Backend returns `credentials`, while older payloads/state may still
    // use `integration_credentials`. Support both to avoid empty edit forms.
    const credentials =
      integration.credentials?.[0]?.credentials ||
      integration.integration_credentials?.[0]?.credentials ||
      {};

    // Set everything in one update to avoid batched-state race conditions.
    setFormData({
      integrationId: integration.id,
      name: integration.name,
      ...credentials
    });

    // Switch to the edit view
    setCurrentView('edit');
  };

  return (
    <div className="min-h-screen bg-white">
      {currentView === 'list' ? (
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
            <button
              onClick={() => {
                setCurrentView('new');
                setFormData({});
              }}
              className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition font-medium"
            >
              Create New Integration
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg mb-8">
              <IntegrationsList
                integrations={integrations}
                onDelete={handleDeleteIntegration}
                onToggleStatus={handleToggleStatus}
                onEdit={handleEditIntegration}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          {/* New/Edit Integration View */}
          <div>
            {/* Header */}
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 mt-2">
                {currentView === 'edit' ? 'Edit Integration' : 'Add New Integration'}
              </h1>
              <p className="text-gray-600">
                {currentView === 'edit' ? 'Update your integration settings' : 'Connect your website with Hyperrank'}
              </p>
            </div>

            {!selectedIntegrationType ? (
              /* Integration Options */
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {integrationOptions.map((integration) => (
                  <div
                    key={integration.id}
                    className="bg-primary-25 hover:bg-gray-100 transition rounded-lg p-6 cursor-pointer flex flex-col items-center justify-center border"
                    onClick={() => setSelectedIntegrationType(integration.id)}
                  >
                    <div className="w-12 h-12 mb-4 flex items-center justify-center">
                      <img src={integration.logo} alt={integration.name} className="w-12 h-12" />
                    </div>
                    <h3 className="text-lg font-medium text-center">{integration.name}</h3>
                  </div>
                ))}
              </div>
            ) : (
              /* Integration Form */
              <div>
                {/* Tabs for different integrations */}
                <div className="flex border-b mb-6">
                  {integrationOptions.map(option => (
                    <button
                      key={option.id}
                      className={`py-2 px-4 font-medium ${selectedIntegrationType === option.id
                        ? 'text-gray-900 border-b-2 border-primary-500'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                      onClick={() => setSelectedIntegrationType(option.id)}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>

                {/* Integration specific form */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                  <div className="mb-4">
                    <div className="bg-primary-50 p-4 mb-6 rounded-lg">
                      <a
                        href="#"
                        className="text-primary-700 font-medium inline-flex items-center"
                      >
                        Watch YouTube tutorial how to integrate your {integrationOptions.find(i => i.id === selectedIntegrationType)?.name} website with Hyperrank
                        <ExternalLink size={16} className="ml-1" />
                      </a>
                    </div>

                    <IntegrationForm
                      integrationType={selectedIntegrationType}
                      formData={formData}
                      setFormData={setFormData}
                      isSubmitting={isSubmitting}
                      handleCreateIntegration={handleCreateIntegration}
                    />

                    <div className="mt-8 flex space-x-4">
                      <button
                        onClick={() => {
                          if (currentView === 'edit') {
                            setCurrentView('list');
                            setFormData({});
                          } else {
                            setSelectedIntegrationType(null);
                          }
                        }}
                        className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                      >
                        {currentView === 'edit' ? 'Cancel' : 'Back'}
                      </button>
                      <button
                        onClick={handleCreateIntegration}
                        disabled={isSubmitting}
                        className="flex-1 bg-primary-500 text-white py-2 px-4 rounded-md hover:bg-primary-600 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {isSubmitting
                          ? (currentView === 'edit' ? 'Updating...' : 'Creating...')
                          : (currentView === 'edit' ? 'Update Integration' : 'Create Integration')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Back to Integrations button */}
            <div className="text-center">
              <button
                onClick={() => {
                  setCurrentView('list');
                  setFormData({});
                  setSelectedIntegrationType(null);
                }}
                className="text-gray-600 hover:text-violet-600 transition"
              >
                &larr; Back to Integrations
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Popup - Centered at top */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-4 shadow-xl border border-gray-200 max-w-sm flex items-center"
            >
              {notification.type === "success" ? (
                <CheckCircle className="text-emerald-500 mr-3 flex-shrink-0" size={20} />
              ) : (
                <AlertCircle className="text-red-500 mr-3 flex-shrink-0" size={20} />
              )}
              <div>
                <h3 className={`text-sm font-medium ${notification.type === "success" ? "text-gray-900" : "text-red-700"}`}>
                  {notification.type === "success" ? "Success!" : "Error"}
                </h3>
                <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IntegrationsPage;